import { useMemo } from 'react'
import { useQAAssessmentContext } from 'contexts/QAAssessmentContext'
import { useQuery } from 'hooks/useQuery'
import {
  BaseCommunicationFragment,
  Get_Comment_CommunicationsQuery,
  Get_Comment_CommunicationsQueryVariables
} from 'generated/graphql'
import { GET_COMMENT_COMMUNICATIONS } from '../getCommunications.query'

interface UseQACommentsProps {
  taskIds: string[]
}

interface UseQACommentsResult {
  commentsLoading: boolean
  commentsError: string | null
  comments: BaseCommunicationFragment[]
  fetchComments: () => Promise<Get_Comment_CommunicationsQuery | undefined>
}

export const useQAComments = ({ taskIds }: UseQACommentsProps): UseQACommentsResult => {
  const { qaAssessmentId } = useQAAssessmentContext()

  const dependency = useMemo(
    () => ({
      taskIds,
      qaAssessmentId
    }),
    [qaAssessmentId, taskIds]
  )

  const { data, isLoading, error, reloadData } = useQuery<
    Get_Comment_CommunicationsQuery,
    Get_Comment_CommunicationsQueryVariables
  >({
    query: GET_COMMENT_COMMUNICATIONS,
    params: {
      taskRef: taskIds.map((taskId) => `Task/${taskId}`).join(','),
      questionnaireRef: `QuestionnaireResponse/${qaAssessmentId}`
    },
    dependency
  })

  const comments = data?.CommunicationList?.filter((c): c is BaseCommunicationFragment => !!c) ?? []

  return {
    comments,
    commentsLoading: isLoading,
    commentsError: error,
    fetchComments: reloadData
  }
}
